import axiosInstance from "../utils/axiosInstance";
import type {
  LandTour,
  LandTourFilterOptions,
  LandTourSearchResult,
  LandTourTag,
} from "../interfaces/land-tour.interface";

// GET /b2c2b/api/v1/land_tours - the B2C ground tour listing (iceholidays#4502).
// Kept separate from the B2B /api/v1/land_tours endpoint, which requires an
// authenticated agent and shows the minimum price instead of the maximum.

export interface LandTourSearchParams {
  keyword?: string;
  /** YYYY-MM-01; the API widens it to the whole month. */
  month?: string;
  /** Category enum keys: private_tour | seat_in_coach. */
  category?: string[];
  guide_languages?: string[];
  /** Inclusion column names: inc_hotel, inc_flight, … */
  inclusions?: string[];
  tag_ids?: string[];
  /** Exact trip lengths (the duration chips). */
  durations?: number[];
  /** Minimum trip length (the duration slider). */
  min_duration?: number;
  min_price?: number;
  max_price?: number;
  /** Destination facet - supported by the API, not yet surfaced in the UI. */
  location_ids?: string[];
  sortBy?: "lowest_price" | "highest_price";
  page?: number;
  per?: number;
}

const toTag = (tag: any): LandTourTag | undefined =>
  tag?.id ? { id: String(tag.id), name: tag.name, description: tag.description } : undefined;

const toNumber = (value: any): number | undefined => {
  // save_amount and price arrive as numeric strings from the JSON:API serializer.
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

class LandToursApi {
  getLandTours(params: LandTourSearchParams): Promise<LandTourSearchResult> {
    return axiosInstance
      // Rack only parses repeated keys into an array when they carry "[]"
      // (`a=1&a=2` collapses to the last value), so every multi-select facet
      // must serialize as `inclusions[]=…&inclusions[]=…`.
      .get("/b2c2b/api/v1/land_tours", { params, paramsSerializer: { indexes: false } })
      .then((response) => {
        const rows = response.data?.data || [];
        const meta = response.data?.meta || {};

        return {
          landTours: rows.map((row: any) => this.mapLandTour(row.attributes || {})),
          total: meta.total_count ?? rows.length,
          totalPages: meta.total_pages ?? 1,
          currentPage: meta.current_page ?? 1,
          filters: this.mapFilters(meta.filters),
        };
      });
  }

  private mapLandTour(attributes: any): LandTour {
    return {
      id: attributes.id,
      code: attributes.code,
      title: attributes.title,
      cnTitle: attributes.cn_title,
      category: attributes.category,
      brand: attributes.brand,
      country: attributes.country,
      days: attributes.days,
      nights: attributes.nights,
      duration: attributes.duration ?? attributes.days,
      minBooking: attributes.min_booking,
      price: toNumber(attributes.price),
      saveAmount: toNumber(attributes.save_amount) ?? 0,
      nextPaxRange: toNumber(attributes.next_pax_range),
      firstDayCity: attributes.first_day_city,
      travelDateStart: attributes.travel_date_start,
      travelDateEnd: attributes.travel_date_end,
      instantConfirmation: Boolean(attributes.instant_confirmation),
      inclusion: attributes.inclusion || {},
      imageUrl: attributes.image_url,
      images: attributes.images || [],
      primaryTag: toTag(attributes.primary_tag),
      otherTags: (attributes.other_tags || []).map(toTag).filter(Boolean) as LandTourTag[],
    };
  }

  private mapFilters(filters: any): LandTourFilterOptions {
    return {
      categories: filters?.categories || [],
      guideLanguages: filters?.guide_languages || [],
      inclusions: filters?.inclusion_options || [],
      promotions: (filters?.promotions || []).map(toTag).filter(Boolean) as LandTourTag[],
      maxDuration: filters?.max_duration || 0,
    };
  }
}

export default new LandToursApi();
